import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BellPlus, Mail, X, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";

/**
 * Modal for creating a custom reminder.
 *
 * Fields:
 *   • Title (required, ≤ 120 chars)
 *   • Message (optional, ≤ 600 chars)
 *   • Trigger date (required, ISO YYYY-MM-DD)
 *   • Email reminder (default true) — when on, an email fires on the
 *     trigger date via Resend
 *
 * On success: invokes onCreated(alertDoc) and closes.
 */
export default function AddAlertDialog({ open, onClose, onCreated }) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [date, setDate] = useState(today);
  const [emailReminder, setEmailReminder] = useState(true);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef(null);

  // Reset form whenever the modal re-opens — avoids stale values from a
  // previously-cancelled session bleeding into a new one.
  useEffect(() => {
    if (open) {
      setTitle("");
      setMessage("");
      setDate(today());
      setEmailReminder(true);
      setBusy(false);
      // Autofocus the title input after the motion enter animation settles.
      setTimeout(() => titleRef.current?.focus(), 80);
    }
  }, [open]);

  // Escape closes the modal (matches the ConfirmDialog UX).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) {
      toast.error("Title is required");
      titleRef.current?.focus();
      return;
    }
    if (!date) {
      toast.error("Pick a trigger date");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/custom-alerts", {
        title: title.trim(),
        message: message.trim(),
        trigger_date: date,
        email_reminder: emailReminder,
      });
      toast.success("Alert added", {
        description: emailReminder
          ? `You'll get an email on ${date}`
          : `You'll see it on the Alerts page when it gets close`,
      });
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error("Could not create alert", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          >
            <form
              onSubmit={submit}
              className="bg-[#0A0A0A] border-t sm:border border-white/[0.12] w-full max-w-lg max-h-[92vh] overflow-y-auto"
              data-testid="add-alert-dialog"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-white/[0.10] sticky top-0 bg-[#0A0A0A] z-10">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                    <BellPlus className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="font-serif text-lg text-white leading-snug">Add custom alert</h2>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/55 mt-1">
                      Reminder · Alerts page + optional email
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-white/65 hover:text-white transition-colors"
                  aria-label="Close"
                  data-testid="add-alert-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                {/* Title */}
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 block mb-1.5">
                    Title <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    ref={titleRef}
                    type="text"
                    maxLength={120}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Renew building insurance"
                    data-testid="add-alert-title"
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2.5 outline-none transition-colors"
                    required
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 block mb-1.5">
                    Trigger date <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="add-alert-date"
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2.5 outline-none transition-colors font-mono"
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 block mb-1.5">
                    Note · optional
                  </label>
                  <textarea
                    maxLength={600}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder="Additional context — appears in the email and on the alert card."
                    data-testid="add-alert-message"
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2.5 outline-none transition-colors resize-none"
                  />
                  <p className="text-[10px] text-white/60 mt-1 text-right">
                    {message.length}/600
                  </p>
                </div>

                {/* Email reminder toggle */}
                <label
                  className="flex items-start gap-3 cursor-pointer p-3 border border-white/[0.10] hover:border-white/[0.12] bg-white/[0.02] transition-colors"
                  data-testid="add-alert-email-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={emailReminder}
                    onChange={(e) => setEmailReminder(e.target.checked)}
                    data-testid="add-alert-email-toggle"
                    className="accent-[#D4AF37] mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="text-white text-sm flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                      Email me on the trigger date
                    </p>
                    <p className="text-white/60 text-[11px] mt-0.5 leading-relaxed">
                      We'll send one Resend email to your account address on the day of the reminder. The alert always appears in your Alerts inbox regardless.
                    </p>
                  </div>
                </label>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/[0.10] bg-[#050505] sticky bottom-0">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  data-testid="add-alert-cancel"
                  className="px-5 py-2 text-xs tracking-[0.15em] uppercase text-white/80 hover:text-white border border-white/10 hover:border-white/25 transition-colors disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !title.trim() || !date}
                  data-testid="add-alert-submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2 text-xs tracking-[0.15em] uppercase font-medium bg-[#D4AF37] hover:bg-[#B5952F] text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check className="w-3 h-3" />
                  {busy ? "Saving…" : "Add alert"}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
